import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Plus, X } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { EmptyState } from '@/components/EmptyState';
import { post } from '@/lib/api';

interface Review {
  id: string;
  employee_name: string;
  reviewer_name: string;
  review_type: string;
  status: string;
  due_date: string;
  overall_rating?: string;
}

export function ReviewsPage() {
  const [status, setStatus] = useState('');
  const navigate = useNavigate();
  const { data, loading } = useApi<{ reviews: Review[] }>(`reviews?status=${status}`, [status]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState({
    employee_id: '',
    employee_name: '',
    reviewer_id: '',
    reviewer_name: '',
    review_type: 'annual',
    due_date: '',
  });

  async function handleCreateReview() {
    if (!reviewForm.employee_id || !reviewForm.reviewer_id || !reviewForm.due_date) {
      setCreateError('Employee ID, reviewer ID, and due date are required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const result = await post<{ id: string }>('reviews', {
        employee_id: reviewForm.employee_id,
        employee_name: reviewForm.employee_name || undefined,
        reviewer_id: reviewForm.reviewer_id,
        reviewer_name: reviewForm.reviewer_name || undefined,
        review_type: reviewForm.review_type,
        due_date: reviewForm.due_date,
      });
      setShowCreate(false);
      setReviewForm({ employee_id: '', employee_name: '', reviewer_id: '', reviewer_name: '', review_type: 'annual', due_date: '' });
      navigate(`/reviews/${result.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create review');
    } finally {
      setCreating(false);
    }
  }

  const inputClass = 'w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Star className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-foreground">Performance Reviews</h2>
        </div>
        <div className="flex items-center gap-3">
          <select value={status} onChange={e => setStatus(e.target.value)} className="bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="self_review">Self Review</option>
            <option value="manager_review">Manager Review</option>
            <option value="completed">Completed</option>
          </select>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
            <Plus className="w-4 h-4" /> Create Review
          </button>
        </div>
      </div>
      {loading ? <div className="text-muted-foreground">Loading...</div> : (
        <DataTable
          columns={[
            { key: 'employee_name', header: 'Employee', render: (r: Review) => <span className="font-medium">{r.employee_name}</span> },
            { key: 'reviewer_name', header: 'Reviewer' },
            { key: 'review_type', header: 'Type', render: (r: Review) => <span className="capitalize">{r.review_type.replace('_', ' ')}</span> },
            { key: 'due_date', header: 'Due Date', render: (r: Review) => r.due_date?.split('T')[0] ?? '' },
            { key: 'overall_rating', header: 'Rating', render: (r: Review) => r.overall_rating ? <span className="capitalize text-xs">{r.overall_rating.replace('_', ' ')}</span> : <span className="text-muted-foreground text-xs">-</span> },
            { key: 'status', header: 'Status', render: (r: Review) => <StatusBadge status={r.status} /> },
          ]}
          data={data?.reviews ?? []}
          onRowClick={(row) => navigate(`/reviews/${row.id}`)}
          emptyNode={
            <EmptyState
              icon={<Star className="w-12 h-12" />}
              title="No reviews yet"
              description="Performance reviews will appear here once created."
              action={
                <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
                  <Plus className="w-4 h-4" /> Create Review
                </button>
              }
            />
          }
        />
      )}

      {/* Create Review Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Create Performance Review</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            {createError && <p className="text-red-400 text-sm">{createError}</p>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Employee ID *</label>
                <input value={reviewForm.employee_id} onChange={e => setReviewForm(f => ({ ...f, employee_id: e.target.value }))} placeholder="Employee ID" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Employee Name</label>
                <input value={reviewForm.employee_name} onChange={e => setReviewForm(f => ({ ...f, employee_name: e.target.value }))} placeholder="John Smith" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Reviewer ID *</label>
                <input value={reviewForm.reviewer_id} onChange={e => setReviewForm(f => ({ ...f, reviewer_id: e.target.value }))} placeholder="Reviewer ID" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Reviewer Name</label>
                <input value={reviewForm.reviewer_name} onChange={e => setReviewForm(f => ({ ...f, reviewer_name: e.target.value }))} placeholder="Jane Doe" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Review Type</label>
                <select value={reviewForm.review_type} onChange={e => setReviewForm(f => ({ ...f, review_type: e.target.value }))} className={inputClass}>
                  <option value="annual">Annual</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="mid_year">Mid-Year</option>
                  <option value="90_day">90-Day</option>
                  <option value="probation">Probation</option>
                  <option value="promotion">Promotion</option>
                  <option value="improvement_plan">Improvement Plan</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Due Date *</label>
                <input type="date" value={reviewForm.due_date} onChange={e => setReviewForm(f => ({ ...f, due_date: e.target.value }))} className={inputClass} />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
              <button onClick={() => void handleCreateReview()} disabled={creating} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
